import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { log } from '@hungryroot/o11y';
import { StatusCode } from '@hungryroot/rest';
import { placeCheckoutOrder } from '@hungryroot/rest/api/place_checkout_order/post';
import {
  getCustomerAddress,
  suggest,
  suggestSecondary,
  saveAddressV2,
  type SuggestionHasSecondaryEntries,
  type AddressRequestBody,
} from './address/main';

const TIME_LIMIT_1_DAY = 1000 * 60 * 60 * 24;

export function useGetAddress() {
  return useQuery({
    queryKey: ['customer-address'],
    queryFn: async () => {
      const result = await getCustomerAddress();

      return result;
    },
    retry: false,
  });
}

export function useSmartyInitialQuery(
  input: string,
  options: { enabled: boolean }
) {
  return useQuery({
    queryKey: ['smarty-address', input],
    queryFn: async () => {
      const { result } = await suggest(input);

      return result;
    },
    ...options,
    gcTime: TIME_LIMIT_1_DAY,
    staleTime: TIME_LIMIT_1_DAY,
    retry: false,
  });
}

export function useSmartySecondaryQuery(
  input: string,
  query: SuggestionHasSecondaryEntries | undefined,
  options: { enabled: boolean }
) {
  return useQuery({
    queryKey: ['smarty-address-secondary', input, query],
    queryFn: async () => {
      if (query === undefined) {
        return;
      }

      const { result } = await suggestSecondary(input, query);

      return result;
    },
    ...options,
    gcTime: TIME_LIMIT_1_DAY,
    staleTime: TIME_LIMIT_1_DAY,
    retry: false,
  });
}

export function usePatchCustomerAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { address: AddressRequestBody }) => {
      const { address } = params;

      const result = await saveAddressV2({ address });
      if (result.status !== StatusCode.SuccessOK) {
        throw result;
      }

      return result.data;
    },
    onSuccess(_, variables) {
      const { address } = variables;
      queryClient.invalidateQueries({
        queryKey: ['customer-address'],
      });
      queryClient.invalidateQueries({
        queryKey: ['Orders', 'order/:id/possible_days', address.zip_code],
      });
    },
    onError(error, variables) {
      const { address } = variables;
      const path = `/api/v2/customers/self/address/`;
      const data = {
        address,
        path,
        error,
      };
      log('v2_patch_address', data);
    },
  });
}

export function usePlaceCheckoutOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { orderId: number; consented?: boolean }) => {
      const { orderId, consented } = params;
      const result = await placeCheckoutOrder({ orderId, consented });
      // If customer already has plan, server returns 400 status and the payment form handles redirecting the user.
      if (
        result.status !== StatusCode.SuccessOK &&
        result.status !== StatusCode.ClientErrorBadRequest
      ) {
        throw result;
      }

      return result.data;
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['Customers', 'self', undefined],
      });
    },
  });
}
